<template>
  <b-container fluid class="mt-3 mt-lg-0">
    <div class="d-flex justify-content-between align-items-center mb-1" style="min-height: 31px">
      <div class="d-flex align-items-center">
        <p class="fs-17 font-weight-bold mb-0 ml-1" style="color: #163D7D">Detalles generales</p>

        <sup v-if="isDev" class="d-flex align-items-center fs-12 text-muted text-right float-right ml-2" v-b-tooltip.hover.v-dark.top="sapStatus.label">
          <font-awesome-icon :icon="['fas', 'dot-circle']" :class="sapStatus.class" class="mr-1" style="font-size: 11px" />
        </sup>
      </div>

      <b-button
        v-if="permissions.can_edit_details"
        variant="outline-secondary"
        size="sm"
        class="d-flex align-items-center px-2"
        style="color: #163D7D; border-color: #c7d0dc; border-radius: 8px"
        @click="$emit('edit')"
      >
        <font-awesome-icon :icon="['far', 'edit']" class="mr-1" />
        Editar
      </b-button>
    </div>

    <b-form-row class="px-1">
      <b-col cols="6" class="mb-2">
        <div>
          <p class="fs-11 font-weight-bold text-uppercase mb-0" style="color: #8a939d; letter-spacing: 0.04em">Fecha requerida</p>
          <span class="fs-14 font-weight-bold text-dark ml-2">{{ drawingRequest.required_date | formatDate }}</span>
        </div>
      </b-col>

      <b-col cols="6" class="mb-2">
        <div>
          <p class="fs-11 font-weight-bold text-uppercase mb-0" style="color: #8a939d; letter-spacing: 0.04em">Estado</p>
          <b-badge :variant="statusVariant" pill class="px-2 flex-shrink-0 mr-2">
            {{ drawingRequest.status_label }}
          </b-badge>
        </div>
      </b-col>

      <b-col cols="6" class="mb-2">
        <div>
          <p class="fs-11 font-weight-bold text-uppercase mb-0" style="color: #8a939d; letter-spacing: 0.04em">Dificultad</p>
          <span v-if="difficultyLevel" class="fs-14 font-weight-bold text-dark ml-2">{{ drawingRequest.difficulty_level_label }}</span>
          <p v-else class="fs-14 font-weight-bold text-muted mb-0 ml-2">Sin definir</p>
        </div>
      </b-col>

      <b-col cols="6" class="mb-2">
        <div class="h-100">
          <p class="fs-11 font-weight-bold text-uppercase mb-0" style="color: #8a939d; letter-spacing: 0.04em">Tiempo proyectado</p>
          <p class="fs-14 font-weight-bold mb-0 ml-2" :class="drawingRequest.estimated_hours ? 'text-dark' : 'text-muted'">
            {{ estimatedHoursText }}
          </p>
        </div>
      </b-col>

      <b-col cols="6" class="mb-2">
        <div>
          <p class="fs-11 font-weight-bold text-uppercase mb-0" style="color: #8a939d; letter-spacing: 0.04em">Sede</p>
          <span v-if="drawingRequest.site" class="fs-14 font-weight-bold text-dark ml-2">{{ drawingRequest.site }}</span>
          <p v-else class="fs-14 font-weight-bold text-muted mb-0 ml-2">Sin definir</p>
        </div>
      </b-col>

      <b-col cols="6" class="mb-2">
        <div class="h-100">
          <p class="fs-11 font-weight-bold text-uppercase mb-0" style="color: #8a939d; letter-spacing: 0.04em">Unidad</p>
          <span v-if="drawingRequest.business_unit" class="fs-14 font-weight-bold text-dark ml-2">{{ drawingRequest.business_unit }}</span>
          <p v-else class="fs-14 font-weight-bold text-muted mb-0 ml-2">Sin definir</p>
        </div>
      </b-col>

      <b-col cols="6" class="mb-2">
        <div class="h-100">
          <p class="fs-11 font-weight-bold text-uppercase mb-0" style="color: #8a939d; letter-spacing: 0.04em">Tipo de documento</p>
          <p v-if="isPresale" class="fs-14 font-weight-bold text-dark mb-0 ml-2">Preventa</p>
          <template v-else>
            <p v-if="drawingRequest.installation_order_number" class="fs-14 font-weight-bold text-dark mb-0 ml-2">
              OI #{{ drawingRequest.installation_order_number }}
            </p>
            <p v-if="drawingRequest.quotation_number" class="fs-14 font-weight-bold text-dark mb-0 ml-2">
              Cot #{{ drawingRequest.quotation_number }}
            </p>
          </template>
        </div>
      </b-col>

      <b-col cols="6" class="mb-2">
        <div class="h-100">
          <p class="fs-11 font-weight-bold text-uppercase mb-0" style="color: #8a939d; letter-spacing: 0.04em">Tipo de plano</p>
          <p class="fs-14 font-weight-bold mb-0 text-dark ml-2">
            {{ drawingRequest.drawing_type_label }}
          </p>
        </div>
      </b-col>
    </b-form-row>

    <div class="px-1">
      <p class="fs-11 font-weight-bold text-uppercase mb-1" style="color: #8a939d; letter-spacing: 0.04em">Descripción</p>
      <p
        v-if="drawingRequest.description"
        class="fs-14 text-dark mb-0"
        :class="{ 'text-clamp-3': !descriptionExpanded }"
        style="white-space: pre-line; line-height: 1.5; cursor: pointer"
        @click="descriptionExpanded = !descriptionExpanded"
      >{{ drawingRequest.description }}</p>
      <p v-else class="fs-14 text-muted mb-0">Sin descripción</p>
    </div>
  </b-container>
</template>

<script>
import { mapGetters } from 'vuex'
import { sapStatuses, statusVariants } from '../../../sharedOptions/drawingRequestOptions'

const DIFFICULTY_ORDER = ['baja', 'media', 'alta', 'experto']

export default {
  name: 'DrawingRequestShowDetails',
  props: {
    drawingRequest: { type: Object, required: true },
    permissions: { type: Object, default: () => ({}) }
  },
  data () {
    return {
      descriptionExpanded: false,
      difficultyLevelCount: DIFFICULTY_ORDER.length
    }
  },
  computed: {
    ...mapGetters(['isDev']),
    isPresale () {
      return this.drawingRequest.associated_documents.includes('preventa')
    },
    sapStatus () {
      return sapStatuses[(this.drawingRequest.sap_sync || {}).status] || sapStatuses.pending
    },
    estimatedHoursText () {
      const hours = this.drawingRequest.estimated_hours
      if (!hours) return 'Sin definir'
      return `${parseFloat(hours)} h`
    },
    difficultyLevel () {
      return DIFFICULTY_ORDER.indexOf(this.drawingRequest.difficulty_level) + 1
    },
    statusVariant () {
      return statusVariants[this.drawingRequest.status] || 'secondary'
    }
  }
}
</script>
